import React, { useEffect, useRef, useState } from "react";
import { createRoot, type Root } from "react-dom/client";
import PolygonManager, { type PolygonManagerHandle } from "./PolygonManager";
import type { RouteForExport } from "./ExportRoutesExcel";
import type { PolygonSelectionSummary } from "./polygon-selection";
import InfoPopup, { type OrderLocation, type InfoPopupProps } from "./InfoPopup";
import { orderPinColor, orderPinIcon } from "./map-order-icon";
import type { DriverAssignment } from "./route-preview";
import type { RouteReviewContext } from "./RoutePreviewDialog";
import type { OptimizedDriverRoute } from "./getOptimizedRoute";
export type { OrderLocation } from "./InfoPopup";

/* ===== Props ===== */
export interface GoogleMapProps {
  lat: number;
  lng: number;
  orders: OrderLocation[];
  focusOrderId?: string | null;
  onFocused?: () => void;
  onRoutesForExportChange?: (routes: RouteForExport[]) => void;
  onOptimizedDriversChange?: (
    drivers: {
      driverId: number;
      driverName: string;
      orderCount: number;
      totalDistanceKm: number;
      totalTaxableAmount: number;
    }[]
  ) => void;
  totalOrderCount: number;
  resizeSignal?: number;
  polygonManagerRef?: React.Ref<PolygonManagerHandle>;
  onSelectionChange?: (summary: PolygonSelectionSummary | null) => void;
  onSelectionBusyChange?: (busy: boolean) => void;
  routeAssignments?: DriverAssignment[];
  reviewRoutes?: (context: RouteReviewContext) => Promise<OptimizedDriverRoute[] | null>;
  drawingEnabled?: boolean;
  autoReview?: boolean;
  onMapReady?: (map: google.maps.Map | null) => void;
  previewOrderIds?: string[];
  drawingControlHost?: HTMLElement | null;
  gpsControlHost?: HTMLElement | null;
  onLoadStateChange?: (state: "loading" | "ready" | "error") => void;
  popupActions?: Pick<InfoPopupProps, "routeTargets" | "includedIds" | "routeActionDisabled" | "onAddToRoute">;
}

export default function GoogleMapComponent({
  lat,
  lng,
  orders,
  focusOrderId,
  onFocused,
  resizeSignal,
  onRoutesForExportChange,
  onOptimizedDriversChange,
  totalOrderCount,
  polygonManagerRef,
  onSelectionChange,
  onSelectionBusyChange,
  routeAssignments, reviewRoutes, drawingEnabled, autoReview, onMapReady, previewOrderIds, drawingControlHost, gpsControlHost, onLoadStateChange, popupActions,
}: GoogleMapProps) {
  const mapRef = useRef<HTMLDivElement | null>(null);
  const map = useRef<google.maps.Map | null>(null);
  const [ready, setReady] = useState(false);
  const loadStateRef = useRef(onLoadStateChange); loadStateRef.current = onLoadStateChange;
  const popupActionsRef = useRef(popupActions); popupActionsRef.current = popupActions;
  const popupRendererRef = useRef(renderPopup); popupRendererRef.current = renderPopup;
  useEffect(() => { if (popupGroupRef.current?.length) popupRendererRef.current(popupGroupRef.current); }, [popupActions]);
  useEffect(() => { if (ready) onMapReady?.(map.current); }, [ready, onMapReady]);

  const markersRef = useRef<google.maps.Marker[]>([]);
  const orderToMarker = useRef<Map<string, google.maps.Marker>>(new Map());
  const [hiddenIds, setHiddenIds] = useState<Set<string>>(new Set());

  const hoppingRef = useRef<google.maps.Marker | null>(null);
  const hopTimer = useRef<number | null>(null);
  const HOP_MS = 1400;

  // InfoWindow with a React root mounted inside
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const popupRootRef = useRef<Root | null>(null);
  const popupDivRef = useRef<HTMLDivElement | null>(null);

  // Popup state (refs so we can re-render without React state)
  const popupSelectedIdRef = useRef<string | null>(null);
  const popupGroupRef = useRef<OrderLocation[] | null>(null);

  /* ---------- Loader ---------- */
  useEffect(() => {
    const load = () =>
      new Promise<void>((resolve, reject) => {
        if (
          (window as any).google?.maps?.drawing &&
          (window as any).google?.maps?.geometry
        ) {
          resolve();
          return;
        }
        const script = document.createElement("script");
        script.src =
          "https://maps.googleapis.com/maps/api/js?key=AIzaSyAoYLkna0JudEVB9imblFTHGJwGLYyw0_k&libraries=places,drawing,geometry&v=3.64";
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error("Google Maps API load error"));
        document.body.appendChild(script);
      });

    let cancelled = false;
    let tilesListener: google.maps.MapsEventListener | undefined;
    const loadTimeout = window.setTimeout(() => { if (!cancelled) loadStateRef.current?.("error"); }, 45000);
    loadStateRef.current?.("loading");
    (async () => {
      await load();
      if (cancelled) return;
      map.current = new google.maps.Map(mapRef.current as HTMLDivElement, {
        center: new google.maps.LatLng(lat, lng),
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: true,
      });
      infoWindowRef.current = new google.maps.InfoWindow();
      tilesListener = google.maps.event.addListenerOnce(map.current, "tilesloaded", () => {
        window.clearTimeout(loadTimeout);
        if (!cancelled) loadStateRef.current?.("ready");
      });
      popupDivRef.current = document.createElement("div");
      popupRootRef.current = createRoot(popupDivRef.current);
      setReady(true);
    })().catch(() => { window.clearTimeout(loadTimeout); if (!cancelled) loadStateRef.current?.("error"); });

    return () => {
      cancelled = true;
      window.clearTimeout(loadTimeout);
      tilesListener?.remove();
      if (hopTimer.current != null) window.clearTimeout(hopTimer.current);
      if (hoppingRef.current) hoppingRef.current.setAnimation(null);

      markersRef.current.forEach((m) => {
        google.maps.event.clearInstanceListeners(m);
        m.setMap(null);
      });
      markersRef.current = [];
      orderToMarker.current.clear();

      infoWindowRef.current?.close();
      infoWindowRef.current = null;
      popupRootRef.current?.unmount();
      popupRootRef.current = null;
      popupDivRef.current = null;
    };
  }, [lat, lng]);

  /* ---------- Helpers ---------- */
  function chooseDefault(group: OrderLocation[]): string {
    // Default: smallest numeric orderId (customize if you want different)
    const sorted = [...group].sort((a, b) => Number(a.orderId) - Number(b.orderId));
    return String(sorted[0].orderId);
  }

  /** Render the React popup with a stable onSelect that only re-renders */
  // function renderPopup(group: OrderLocation[]) {
  //   if (!popupRootRef.current) return;

  //   popupRootRef.current.render(
  //     <InfoPopup
  //       group={group}
  //       selectedId={popupSelectedIdRef.current!}
  //       onSelect={(id) => {
  //         popupSelectedIdRef.current = id;   // single click updates selection
  //         renderPopup(group);                 // re-render content in-place
  //       }}
  //     />
  //   );
  // }

  function renderPopup(group: OrderLocation[]) {
  if (!popupRootRef.current) return;

  popupRootRef.current.render(
    <InfoPopup
      {...popupActionsRef.current}
      group={group}
      selectedId={popupSelectedIdRef.current!}
      onSelect={(id) => {
        popupSelectedIdRef.current = id;   // single click updates selection
        renderPopup(group);                // re-render content in-place
      }}
      onClose={() => {
        popupGroupRef.current = [];
        infoWindowRef.current?.close();    // ✅ closes the Google InfoWindow
      }}
    />
  );
}


  function openPopup(
    marker: google.maps.Marker,
    group: OrderLocation[],
    initialSelectedId?: string | null
  ) {
    if (!infoWindowRef.current || !map.current || !popupRootRef.current || !popupDivRef.current) return;

    popupGroupRef.current = group;

    if (initialSelectedId && group.some(o => String(o.orderId) === String(initialSelectedId))) {
      popupSelectedIdRef.current = String(initialSelectedId);
    } else {
      const keepCurrent =
        popupSelectedIdRef.current &&
        group.some(o => String(o.orderId) === String(popupSelectedIdRef.current));
      if (!keepCurrent) {
        popupSelectedIdRef.current = chooseDefault(group);
      }
    }

    renderPopup(group);
    infoWindowRef.current.setContent(popupDivRef.current);
    infoWindowRef.current.open({ anchor: marker, map: map.current, shouldFocus: false });
  }

  /* ---------- Markers ---------- */
  useEffect(() => {
    if (!ready || !map.current) return;

    // Clear old
    markersRef.current.forEach((m) => {
      google.maps.event.clearInstanceListeners(m);
      m.setMap(null);
    });
    markersRef.current = [];
    orderToMarker.current.clear();

    // Group orders by exact lat,lng
    const byLoc = new Map<string, OrderLocation[]>();
    for (const o of orders) {
      const key = `${o.lat},${o.lng}`;
      if (!byLoc.has(key)) byLoc.set(key, []);
      byLoc.get(key)!.push(o);
    }

    byLoc.forEach((group, key) => {
      const visibleGroup = group.filter(o => !hiddenIds.has(String(o.orderId)) && !previewOrderIds?.includes(String(o.orderId)));
      if (!visibleGroup.length) return;

      const [la, ln] = key.split(",");
      const position = { lat: parseFloat(la), lng: parseFloat(ln) };

      const color = orderPinColor(visibleGroup);

      const marker = new google.maps.Marker({
        map: map.current!,
        position,
        icon: orderPinIcon(color),
        zIndex: 100,
        title: visibleGroup.map(o => `${o.orderId}: ${o.customerName}`).join("\n"),
      });

      group.forEach((o) => orderToMarker.current.set(String(o.orderId), marker));
      markersRef.current.push(marker);

      // Direct marker click → use default for that group
      marker.addListener("click", () => {
        openPopup(marker, visibleGroup, undefined);

        const pos = marker.getPosition();
        if (pos) {
          map.current!.panTo(pos);
          if ((map.current!.getZoom() ?? 12) < 14) map.current!.setZoom(14);
        }
      });
    });
  }, [orders, ready, hiddenIds, previewOrderIds]);

  /* ---------- Focus hop + open popup (from right panel, etc.) ---------- */
  useEffect(() => {
    if (!focusOrderId || !map.current) return;
    const marker = orderToMarker.current.get(String(focusOrderId));
    if (!marker) return;

    // stop previous animation
    if (hoppingRef.current && hoppingRef.current !== marker) {
      hoppingRef.current.setAnimation(null);
    }
    if (hopTimer.current != null) {
      window.clearTimeout(hopTimer.current);
      hopTimer.current = null;
    }

    // bounce + pan
    marker.setAnimation(google.maps.Animation.BOUNCE);
    hoppingRef.current = marker;

    const pos = marker.getPosition();
    if (pos) {
      map.current!.panTo(pos);
      if ((map.current!.getZoom() ?? 12) < 14) map.current!.setZoom(14);
    }

    // find the group at this marker and open with the focused id preselected
    const latR = Number(pos!.lat().toFixed(6));
    const lngR = Number(pos!.lng().toFixed(6));
    const groupVisible = orders.filter(
      (o) =>
        !hiddenIds.has(String(o.orderId)) &&
        Number(o.lat.toFixed(6)) === latR &&
        Number(o.lng.toFixed(6)) === lngR
    );
    const groupForPopup =
      groupVisible.length
        ? groupVisible
        : [orders.find(o => String(o.orderId) === String(focusOrderId))!];

    openPopup(marker, groupForPopup, String(focusOrderId));

    hopTimer.current = window.setTimeout(() => {
      marker.setAnimation(null);
      if (hoppingRef.current === marker) hoppingRef.current = null;
      hopTimer.current = null;
      onFocused?.();
    }, HOP_MS);
  }, [focusOrderId, onFocused, orders, hiddenIds]);

  /* ---------- Resize ---------- */
  useEffect(() => {
    if (map.current && (window as any).google?.maps) {
      google.maps.event.trigger(map.current, "resize");
    }
  }, [resizeSignal]);

  /* ---------- Routed/Unrouted handlers ---------- */
  const handleOrdersRouted = (orderIds: (string | number)[]) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      orderIds.forEach((id) => next.add(String(id)));
      return next;
    });
  };
  const handleOrdersUnrouted = (orderIds: (string | number)[]) => {
    setHiddenIds((prev) => {
      const next = new Set(prev);
      orderIds.forEach((id) => next.delete(String(id)));
      return next;
    });
  };
  const handleClearRouted = () => setHiddenIds(new Set());

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      <div ref={mapRef} style={{ width: "100%", height: "100%" }} />
      {ready && map.current && (
        <PolygonManager
          ref={polygonManagerRef}
          map={map.current}
          orders={orders}
          totalOrderCount={totalOrderCount}
          onOrdersRouted={handleOrdersRouted}
          onOrdersUnrouted={handleOrdersUnrouted}
          onClearRouted={handleClearRouted}
          onRoutesForExportChange={onRoutesForExportChange}
          onOptimizedDriversChange={onOptimizedDriversChange}
          onSelectionChange={onSelectionChange}
          onSelectionBusyChange={onSelectionBusyChange}
          routeAssignments={routeAssignments} reviewRoutes={reviewRoutes} drawingEnabled={drawingEnabled} autoReview={autoReview}
          drawingControlHost={drawingControlHost} gpsControlHost={gpsControlHost}
        />
      )}
    </div>
  );
}
